import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Evento, FilterItem, Ordine, SearchResult, WorkSession} from '../_models/_Index';
import {environment} from '../../environments/environment';
import {OrganizzazioniService} from './organizzazioni.service';
import {StatoEvento} from '../_helpers/stato-evento';
import {StatoEnum} from '../_models/generated/stato-enum';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {SaveMessage} from '../_helpers/save-message';
import {AuthenticationService} from './authentication.service';
import {shouldShowNotificationForActor} from '../_helpers/notification-actor.helper';

@Injectable({providedIn: 'root'})
export class EventiService {

  public lastEventoCreated: BehaviorSubject<Evento> = new BehaviorSubject<Evento>(null);
  public lastEventoUpdated: BehaviorSubject<Evento> = new BehaviorSubject<Evento>(null);
  public lastEventoDeleted: BehaviorSubject<Evento> = new BehaviorSubject<Evento>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {}

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/evento`, this.hubState);

    this.hubConnection.on('SendCreated', (evento: Evento) => {
      this.handleCreatedEvento(evento);
    });

    this.hubConnection.on('SendUpdated', (evento: Evento) => {
      if (evento.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        if (this.shouldShowUpdateNotification(evento.executedBy)) {
          this.saveMessage.entityUpdated('evento', evento);
        }
        this.lastEventoUpdated.next(evento);
      }
    });

    this.hubConnection.on('SendDeleted', (evento: Evento) => {
      if (evento.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.entityDeleted('evento', evento);
        this.lastEventoDeleted.next(evento);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  async getEventiFilteredAsyn(orgid: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean, filters: FilterItem[]) {
    return firstValueFrom(
      this.http.post<SearchResult<Evento>>(`${environment.localApiUrl}api/Eventi/EventiFiltered/${orgid}`,
        {search, page, count, sortField, sortAsc, filters})
    );
  }

  async getEventoAsyn(orgId: number, idEvento: number) {
    return firstValueFrom(this.http.get<Evento>(`${environment.localApiUrl}api/Eventi/Evento/${orgId}/${idEvento}`));
  }

  async createEventoAsync(evento: Evento) {
    evento.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Evento>(`${environment.localApiUrl}api/Eventi/CreateEvento`, evento));
  }

  async creaUserEvento(stato: number, organizzazioneId: number, dataEvento?: Date) {
    const evento: Evento = new Evento();
    evento.isActive = true;
    evento.organizzazioneId = organizzazioneId;
    evento.tipoEvento = StatoEvento.INFO;
    evento.statoRisorsa = stato;
    evento.dataRegistrazione = dataEvento;
    evento.descrizione = `${StatoEnum[stato]} user`;
    evento.executedBy = this.authService.currentUserValue.userName;

    return this.createEventoAsync(evento);
  }

  async creaEvento(numEvento: number, ordine: Ordine, dataEvento?: Date) {
    //const utcSeconds = parseInt(((dataEvento.getTime() + dataEvento.getTimezoneOffset()*60*1000)/1000).toString(), 10);
    const utcSeconds = parseInt((dataEvento.getTime() / 1000).toString(), 10);
    const evento: Evento = new Evento();
    evento.isActive = true;
    evento.organizzazioneId = ordine.organizzazioneId;
    evento.tipoEvento = StatoEvento.INFO;
    evento.idDocumento = ordine.idDocumento;
    evento.idRiga = ordine.idRiga;
    evento.idRigaLavorazione = ordine.idRigaLavorazione;
    evento.risorsaId = ordine.risorsaId;
    evento.codiceRisorsa = ordine.risorsa ? ordine.risorsa.codice : '';
    evento.codiceArticolo = ordine.codiceArticolo;
    evento.statoRisorsa = numEvento;
    evento.dataUnixRegistrazione = utcSeconds;
    evento.dataRegistrazione = dataEvento;
    evento.descrizione = `${StatoEnum[numEvento]} ${ordine.idDocumento}.${ordine.idRiga}.${ordine.idRigaLavorazione}`;
    evento.contesto = 0;
    evento.rifEsterno = ordine.rifEsterno;
    evento.note = JSON.stringify(ordine);
    evento.executedBy = this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue.userName;

    return this.createEventoAsync(evento);
  }

  async updateEventoAsync(evento: Evento) {
    evento.executedBy ??= this.getNotificationActor();
    return firstValueFrom(
      this.http.post<Evento>(`${environment.localApiUrl}api/Eventi/UpdateEvento`, evento)
    );
  }

  async deleteEventoAsync(orgId: number, id: number) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `?executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<Evento>(`${environment.localApiUrl}api/Eventi/DeleteEvento/${orgId}/${id}${actorQuery}`));
  }

  async deleteEventoPostDateAsync(orgId: number, risorsaId: number) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `?executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<Evento>(`${environment.localApiUrl}api/Eventi/DeleteEventoPosticipate/${orgId}/${risorsaId}${actorQuery}`));
  }

  async getWorkSessions(startDate: Date, endDate: Date, orgId: number, risorsaId: number) {
    return firstValueFrom(this.http.post<WorkSession[]>(`${environment.localApiUrl}api/Eventi/GetWorkSessions`,
      {startDate, endDate, orgId, risorsaId}));
  }

  private getNotificationActor(): string | undefined {
    return this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue?.userName;
  }

  private shouldShowUpdateNotification(executedBy?: string): boolean {
    return shouldShowNotificationForActor(executedBy, [this.organizzazioniService.codiceOperatore, this.authService.currentUserValue?.userName]);
  }

  private handleCreatedEvento(evento: Evento): void {
    if (evento.organizzazioneId !== this.organizzazioniService.organizzazioneId) {
      return;
    }

    if (this.isUserAuditEvent(evento)) {
      this.saveMessage.entityCreated('evento', evento);
    }
    this.lastEventoCreated.next(evento);
  }

  private isUserAuditEvent(evento: Evento): boolean {
    return evento.statoRisorsa === StatoEnum.REGISTER ||
      evento.statoRisorsa === StatoEnum.USER_DELETED ||
      evento.statoRisorsa === StatoEnum.PASSWORD_CHANGED;
  }
}
